/**
 * Zentraler Zustand der Oberfläche.
 *
 * Zwei Datenquellen greifen ineinander:
 *  - `world`: der vollständige Zustand, per REST geladen, wenn eine Detailansicht
 *    ihn braucht. Er ist groß und wird nicht bei jedem Tick übertragen.
 *  - `live`: der kompakte Auszug, der über WebSocket bei jedem Tick eintrifft.
 *
 * Ansichten lesen Kennzahlen aus `live` (immer aktuell) und Detaildaten aus
 * `world` (bei Bedarf nachgeladen). Das hält die Übertragung auch bei
 * fünfstelligen Kundenzahlen klein.
 */
import { useMemo } from 'react';
import { create } from 'zustand';
import {
  AREA_UNLOCKS,
  activeModifiers,
  areasUnlockedBy,
  serverLoadRows,
  type Alert,
  type Command,
  type CommandResult,
  type GameEvent,
  type LogEntry,
  type ProgressSummary,
  type ServerLoadRow,
  type World,
} from '@hosting-sim/core';
import { api, type GameRole } from '../lib/api';
import { useAreaVisibility } from './auth';
import { AREA_LABELS } from '../lib/navigation';
import { canControlTime, canRunCommand } from '../lib/permissions';
import { connectSocket, disconnectSocket, type LivePayload } from '../lib/socket';

export interface Toast {
  id: number;
  kind: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

/**
 * Verpasste Aufträge, gesammelt je Produkt.
 *
 * Der Tickbericht meldet Ablehnungen einzeln und in jedem Tick neu. Einzeln
 * eingeblendet wäre das Dauerlärm, weggeworfen – der bisherige Zustand – bleibt
 * der Umsatzverlust unsichtbar: Der Betreiber sieht Server stehen und schließt
 * auf fehlende Nachfrage, während in Wahrheit stündlich Aufträge abprallen.
 */
export interface VerpassterAuftrag {
  product: string;
  reason: string;
  count: number;
  /** Tick der letzten Einblendung – begrenzt die Meldung auf einmal je Spieltag. */
  lastToastTick: number;
}

interface GameState {
  gameId: string | null;
  world: World | null;
  live: LivePayload | null;
  /** Eigene Rolle in der offenen Partie; `null` vor dem ersten Laden. */
  role: GameRole | null;
  /** Rollierendes Protokoll der letzten Ticks, für die Live-Anzeige. */
  recentLogs: LogEntry[];
  loading: boolean;
  error: string | null;
  toasts: Toast[];

  open: (gameId: string) => Promise<void>;
  close: () => void;
  refreshWorld: () => Promise<void>;
  /**
   * Kommando ausführen. `quiet` unterdrückt die Einblendung – für die Konsole,
   * die das Ergebnis selbst anzeigt.
   */
  run: (command: Command, options?: { quiet?: boolean }) => Promise<CommandResult>;
  setRunning: (running: boolean, speed?: number) => Promise<void>;
  fastForward: (ticks: number) => Promise<void>;
  toast: (kind: Toast['kind'], message: string) => void;
  dismissToast: (id: number) => void;
}

let toastCounter = 0;

/**
 * Zieht die Kennzahlen des Live-Auszugs auf einen frisch geladenen Weltzustand
 * nach.
 *
 * `useMetrics` bevorzugt `live`, weil dieser Auszug im Normalfall der jüngere
 * ist. Nach einem REST-Abruf gilt das Gegenteil: Dann ist `world` der neuere
 * Stand, und ein unveränderter `live` würde ihn wieder überdecken.
 */
function syncLive(live: LivePayload | null, world: World): LivePayload | null {
  return live ? { ...live, meta: world.meta, company: world.company, derived: world.derived } : live;
}

/**
 * Ändert die Zeitsteuerung in *beiden* Quellen.
 *
 * Nur `world` zu ändern genügt nicht: Solange ein Live-Auszug vorliegt, liest
 * die Oberfläche aus ihm – die Änderung bliebe unsichtbar.
 */
function withMeta(state: GameState, patch: Partial<World['meta']>): Pick<GameState, 'world' | 'live'> {
  return {
    world: state.world ? { ...state.world, meta: { ...state.world.meta, ...patch } } : state.world,
    live: state.live ? { ...state.live, meta: { ...state.live.meta, ...patch } } : state.live,
  };
}

export const useGame = create<GameState>((set, get) => ({
  gameId: null,
  world: null,
  live: null,
  role: null,
  recentLogs: [],
  loading: false,
  error: null,
  toasts: [],

  async open(gameId) {
    set({ loading: true, error: null, gameId, role: null });
    try {
      const [world, { role }] = await Promise.all([api.state(gameId), api.myRole(gameId)]);
      set({ world, role, loading: false });

      connectSocket(gameId, (payload) => {
        set((state) => {
          // Kennzahlen im geladenen Weltzustand mitziehen, damit Detailansichten
          // nicht mit veralteten Werten arbeiten, solange sie offen sind.
          const world = state.world
            ? {
                ...state.world,
                meta: payload.meta,
                company: payload.company,
                derived: payload.derived,
                monitoring: { ...state.world.monitoring, ...payload.monitoring },
              }
            : state.world;

          const merged = [...state.recentLogs, ...payload.logs];
          return {
            live: payload,
            world,
            recentLogs: merged.slice(-300),
          };
        });

        // Ein erfülltes Ziel ist der seltene Fall, in dem eine Einblendung
        // angebracht ist: Es passiert höchstens einmal pro Spieltag und ist die
        // Belohnung für eine Entscheidung, die Stunden zurückliegt.
        // Nur die wichtigen Meldungen werden eingeblendet. Der Rest steht in
        // der Fachpresse – eine Welt, die jede Regung einblendet, wird zum Lärm.
        for (const news of payload.report?.news ?? []) {
          if (news.relevance === 'wichtig') get().toast('info', news.headline);
        }

        // Ein abgeschlossenes Geschäftsjahr ist der seltenste Anlass im Spiel –
        // er verdient einen Hinweis, aber keinen Dialog, der die Partie anhält.
        if (payload.report?.closedYear) {
          get().toast('info', `Geschäftsjahr abgeschlossen – der Jahresbericht liegt in der Chronik.`);
        }

        for (const objective of payload.report?.completedObjectives ?? []) {
          get().toast(
            'success',
            objective.kind === 'campaign' ? `Ziel erreicht: ${objective.title}` : `Erfolg: ${objective.title}`,
          );

          // Freigeschaltete Bereiche eigens melden. Ein neuer Menüpunkt, der
          // wortlos auftaucht, wird übersehen – angekündigt ist er eine Belohnung.
          const areas = areasUnlockedBy(objective.id)
            .map((page) => AREA_LABELS[page])
            .filter(Boolean);
          if (areas.length > 0) {
            get().toast('info', `Neu verfügbar: ${areas.join(', ')}`);
          }
        }
      });
    } catch (error) {
      set({ loading: false, error: (error as Error).message });
    }
  },

  close() {
    disconnectSocket();
    set({ gameId: null, world: null, live: null, role: null, recentLogs: [] });
  },

  async refreshWorld() {
    const { gameId } = get();
    if (!gameId) return;
    try {
      const world = await api.state(gameId);
      set((state) => ({ world, live: syncLive(state.live, world) }));
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async run(command, options) {
    const { gameId, role } = get();
    if (!gameId) return { ok: false, message: 'Keine Partie geöffnet.' };
    // Dieselbe Prüfung wie im Controller, nur schon vor dem Netzabruf: eine
    // gesperrte Rolle bekommt eine sofortige, verständliche Meldung statt
    // eines Fehlers, der erst nach einem Umweg über den Server eintrifft.
    if (!canRunCommand(role, command.type)) {
      const message =
        role === 'CUSTOMER' ? 'Kunden führen keine Aktionen im Unternehmen aus.' : 'Diese Aktion ist der Rolle Support nicht erlaubt.';
      if (!options?.quiet) get().toast('error', message);
      return { ok: false, message };
    }
    try {
      const result = await api.command(gameId, command);
      if (!options?.quiet) get().toast(result.ok ? 'success' : 'error', result.message);
      // Nach einer erfolgreichen Änderung den Weltzustand nachladen; die
      // Live-Daten enthalten bewusst nur Kennzahlen. Reine Abfragen ändern nichts.
      if (result.ok && !result.silent) await get().refreshWorld();
      return result;
    } catch (error) {
      const message = (error as Error).message;
      if (!options?.quiet) get().toast('error', message);
      return { ok: false, message };
    }
  },

  async setRunning(running, speed) {
    const { gameId } = get();
    if (!gameId) return;

    const before = get().live?.meta ?? get().world?.meta;
    // Sofort umschalten, nicht erst nach der Antwort: Die Schaltfläche soll dem
    // Klick folgen und nicht der Laufzeit des Netzabrufs. Der Server schickt
    // gleich darauf denselben Stand nach und bestätigt damit die Annahme.
    set((state) => withMeta(state, { paused: !running, speed: speed ?? state.live?.meta.speed ?? state.world?.meta.speed }));

    try {
      await api.setRunning(gameId, running, speed);
    } catch (error) {
      // Fehlgeschlagen: die vorweggenommene Änderung zurücknehmen, sonst zeigt
      // die Oberfläche einen Zustand, den der Server nie angenommen hat.
      if (before) set((state) => withMeta(state, { paused: before.paused, speed: before.speed }));
      get().toast('error', (error as Error).message);
    }
  },

  async fastForward(ticks) {
    const { gameId } = get();
    if (!gameId) return;
    try {
      const result = await api.fastForward(gameId, ticks);
      await get().refreshWorld();
      get().toast(
        'info',
        `${result.ticks} Stunden vorgespult: ${result.newCustomers} neue Kunden, ${result.lostCustomers} Kündigungen` +
          (result.events.length ? `, Ereignisse: ${result.events.join(', ')}` : ''),
      );
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  toast(kind, message) {
    const id = ++toastCounter;
    set((state) => ({ toasts: [...state.toasts, { id, kind, message }] }));
    // Erfolgsmeldungen verschwinden von selbst, Fehler bleiben länger stehen.
    setTimeout(() => get().dismissToast(id), kind === 'error' ? 9000 : 4500);
  },

  dismissToast(id) {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
}));

/** Bequemer Zugriff auf die aktuellen Kennzahlen, egal ob live oder geladen. */
export function useMetrics(): { derived: World['derived']; meta: World['meta']; company: World['company'] } | null {
  const live = useGame((s) => s.live);
  const world = useGame((s) => s.world);
  if (live) return { derived: live.derived, meta: live.meta, company: live.company };
  if (world) return { derived: world.derived, meta: world.meta, company: world.company };
  return null;
}

/**
 * Zielfortschritt aus dem Live-Auszug.
 *
 * Nur die Kurzfassung – der vollständige Katalog wird von der Zielseite über
 * REST geholt. Er ändert sich höchstens einmal pro Spieltag und gehört deshalb
 * nicht in einen Auszug, der sekündlich über die Leitung geht.
 */
export function useProgress(): ProgressSummary | null {
  return useGame((s) => s.live?.progress ?? null);
}

/**
 * Prüft, ob ein Oberflächenbereich freigeschaltet ist.
 *
 * Die Regeln liegen im Simulationskern (`AREA_UNLOCKS`), nicht hier: Sie hängen
 * an den Kampagnenzielen, und zwei Quellen für dieselbe Bedingung würden
 * unweigerlich auseinanderlaufen.
 *
 * Solange der Fortschritt noch nicht eingetroffen ist, gilt alles als offen.
 * Andersherum wäre die Seitenleiste beim Laden für einen Moment leer.
 */
export function useUnlocked(): (page: string) => boolean {
  const showAll = useAreaVisibility((s) => s.showAll);
  const completed = useGame((s) => s.live?.progress.completed ?? s.world?.progress.completed ?? null);

  return (page: string) => {
    if (showAll || !completed) return true;
    const required = AREA_UNLOCKS[page];
    return !required || completed[required] !== undefined;
  };
}

/**
 * Auslastung je Server, nach Server-ID nachschlagbar.
 *
 * Liegt kein Live-Auszug vor – etwa direkt nach dem Öffnen einer Partie –,
 * wird sie aus dem geladenen Weltzustand gerechnet. Dieselbe Funktion, dieselbe
 * Rundung; nur eben aus zweiter Hand.
 */
export function useServerLoad(): Map<string, ServerLoadRow> {
  const rows = useGame((s) => s.live?.servers);
  const world = useGame((s) => s.world);

  return useMemo(() => {
    const source = rows ?? (world ? serverLoadRows(world, activeModifiers(world).computePerformance) : []);
    return new Map(source.map((row) => [row.id as string, row]));
  }, [rows, world]);
}

export function useAlerts(): Alert[] {
  const metrics = useMetrics();
  return metrics?.derived.alerts ?? [];
}

/** Eigene Rolle in der offenen Partie. */
export function useGameRole(): GameRole | null {
  return useGame((s) => s.role);
}

/** Darf die eigene Rolle Zeitsteuerung und Spielstände anfassen? */
export function useCanControlTime(): boolean {
  return canControlTime(useGame((s) => s.role));
}

export function useActiveEvents(): GameEvent[] {
  const live = useGame((s) => s.live);
  const world = useGame((s) => s.world);
  if (live) return live.events;
  return world ? Object.values(world.events).filter((e) => !e.resolved) : [];
}
